import React, { useState } from 'react'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import IconButton from '@mui/material/IconButton'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Tooltip from '@mui/material/Tooltip'
import Stack from '@mui/material/Stack'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Edit'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import AddIcon from '@mui/icons-material/Add'
import FolderIcon from '@mui/icons-material/Folder'
import { truncateString } from '../../../helpers'

const PathsSection = ({
  saveConfigChanges, paths, setPaths, makeNewConfig, configToEdit, setConfigToEdit, selectedPath,
  setSelectedPath, setRegexValue, checkFiltersOnInput, testInput, makeRegexString, setForwardingRules
}) => {
  const [editingPath, setEditingPath] = useState()

  const addNewPath = () => {
    const lastPath = configToEdit.filters[configToEdit.filters.length - 1]?.path
    if (lastPath !== undefined && lastPath === '') return

    const newConfig = {
      ...configToEdit,
      filters: [
        ...configToEdit.filters,
        { path: 'C:\\Users\\%username%\\', fileNameFiltersToExclude: [], fileExtensionsToInclude: [], localSilentBlacklist: [], forwardingRules: [], inclusionRegex: '', docType: '' }
      ]
    }
    setConfigToEdit(newConfig)
    saveConfigChanges(newConfig)
    setPaths([...paths, { path: '', index: paths.length }])
    setEditingPath(paths.length)
    setTimeout(() => document.getElementById(`editing-path-${paths.length}`)?.focus(), 100)
  }

  const handlePathSelectionOnClick = (item, key) => {
    if (item.path.length > 0) {
      setRegexValue(makeRegexString(configToEdit.filters[key].inclusionRegex))
      setSelectedPath(paths[key])
      setForwardingRules(configToEdit.filters[key].forwardingRules)
      checkFiltersOnInput(testInput, key)
    } else {
      setEditingPath(key)
      setTimeout(() => document.getElementById(`editing-path-${key}`)?.focus(), 100)
    }
  }

  const savePathEditChanges = (e, key, item, newPathValue) => {
    const duplicatePaths = configToEdit.filters.filter((f, i) => f.path === newPathValue && i !== key)
    const duplicateDocTypes = duplicatePaths?.filter((f, i) => f.docType === configToEdit.filters[key].docType && i !== key)

    if (duplicateDocTypes?.length > 0) {
      window.growl.show({ severity: 'error', summary: 'Error', detail: 'There is already a path of this value with this doc type.' })
      return
    }

    if (paths.filter((item, i) => item.path === '' && i === key).length > 0 && newPathValue === '') {
      window.growl.show({ severity: 'error', summary: 'Error', detail: 'Empty Path' })
      return
    }

    setEditingPath()

    if ((!newPathValue || newPathValue === '') && paths.length > 1) {
      const newConfig = { ...configToEdit, filters: configToEdit.filters.filter((f, i) => i !== key) }
      setConfigToEdit(newConfig)
      saveConfigChanges(newConfig)
      setRegexValue(makeRegexString(configToEdit.filters[0].inclusionRegex), 0)
      setSelectedPath(paths[0])
      setPaths(paths.filter((p, i) => i !== key))
      return
    }

    setPaths(paths.map((path, i) => (i === key ? { index: i, path: newPathValue } : path)))

    const newConfig = {
      ...configToEdit,
      filters: configToEdit.filters.map((filter, i) => (i === key ? { ...filter, path: newPathValue } : filter))
    }

    setConfigToEdit(newConfig)
    saveConfigChanges(newConfig)
    checkFiltersOnInput(testInput, null, newConfig)

    e.stopPropagation()
    e.preventDefault()
  }

  const cancelPathEdit = (e, newPathValue, initialValue, key) => {
    setPaths(paths.map((path, i) => (i === key ? { index: i, path: initialValue } : path)))

    const newConfig = {
      ...configToEdit,
      filters: configToEdit.filters.map((filter, i) => (i === key ? { ...filter, path: initialValue } : filter))
    }

    setConfigToEdit(newConfig)
    saveConfigChanges(newConfig)
    setEditingPath()

    e.stopPropagation()
    e.preventDefault()
  }

  const handlePathDeletion = (e, key) => {
    if (paths.length === 1) return

    const newConfig = {
      ...configToEdit,
      filters: configToEdit.filters.filter((f, i) => i !== key)
    }

    setConfigToEdit(newConfig)
    saveConfigChanges(newConfig)
    setPaths(paths.filter((p, i) => i !== key))

    if (selectedPath.index === key) {
      setSelectedPath(paths[key - 1 < 0 ? 0 : key - 1])
    }

    e.preventDefault()
    e.stopPropagation()
  }

  const PathEditor = ({ index, item, isSelected }) => {
    const initialValue = item.path || ''
    const [newPathValue, setNewPathValue] = useState(item.path || '')

    return (
      <Stack direction='row' spacing={0.5} sx={{ width: '100%', alignItems: 'center' }}>
        <TextField
          id={`editing-path-${index}`}
          value={newPathValue}
          onChange={(e) => setNewPathValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') savePathEditChanges(e, index, item, newPathValue) }}
          placeholder='C:\Users\%username%\ oppure \\server\cartella\'
          size='small'
          fullWidth
          autoFocus
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
        />
        <Tooltip title='Conferma' arrow>
          <IconButton
            size='small'
            onClick={(e) => savePathEditChanges(e, index, item, newPathValue)}
            color={isSelected ? undefined : 'primary'}
            sx={isSelected ? { color: 'white' } : {}}
            aria-label='Conferma modifica percorso'
          >
            <CheckIcon fontSize='small' />
          </IconButton>
        </Tooltip>
        <Tooltip title='Annulla' arrow>
          <IconButton
            size='small'
            onClick={(e) => cancelPathEdit(e, newPathValue, initialValue, index)}
            sx={isSelected ? { color: 'white' } : {}}
            aria-label='Annulla modifica percorso'
          >
            <CloseIcon fontSize='small' />
          </IconButton>
        </Tooltip>
      </Stack>
    )
  }

  const PathItem = ({ index, item }) => {
    const isSelected = selectedPath.index === index
    const docType = configToEdit.filters[index]?.docType

    return (
      <Paper
        elevation={0}
        onClick={() => { if (!isSelected) handlePathSelectionOnClick(item, index) }}
        sx={{
          p: 1.5,
          borderRadius: 2,
          cursor: 'pointer',
          border: '1px solid',
          borderColor: isSelected ? 'primary.main' : 'divider',
          bgcolor: isSelected ? 'primary.main' : 'background.paper',
          color: isSelected ? 'white' : 'text.primary',
          transition: 'all 0.15s ease',
          '&:hover': {
            borderColor: 'primary.main',
            bgcolor: isSelected ? 'primary.main' : 'action.hover'
          }
        }}
      >
        {editingPath === index
          ? <PathEditor index={index} item={item} isSelected={isSelected} />
          : (
            <Stack direction='row' alignItems='center' spacing={1}>
              <FolderIcon sx={{ fontSize: 18, opacity: 0.7 }} />
              <Typography
                variant='body2'
                sx={{ flex: 1, fontWeight: isSelected ? 600 : 400, wordBreak: 'break-all' }}
              >
                {truncateString(item.path)}
              </Typography>
              {docType && (
                <Chip
                  label={docType}
                  size='small'
                  sx={{
                    fontWeight: 600,
                    fontSize: 11,
                    height: 22,
                    bgcolor: isSelected ? 'rgba(255,255,255,0.2)' : 'primary.light',
                    color: isSelected ? 'white' : 'primary.contrastText'
                  }}
                />
              )}
              <Tooltip title='Modifica percorso' arrow>
                <IconButton
                  size='small'
                  onClick={(e) => { setEditingPath(index); e.preventDefault(); e.stopPropagation() }}
                  sx={{ color: isSelected ? 'white' : 'text.secondary' }}
                  aria-label='Modifica percorso'
                >
                  <EditIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              {paths.length > 1 && (
                <Tooltip title='Elimina percorso' arrow>
                  <IconButton
                    size='small'
                    onClick={(e) => handlePathDeletion(e, index)}
                    sx={{ color: isSelected ? 'rgba(255,255,255,0.7)' : 'error.light' }}
                    aria-label='Elimina percorso'
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          )}
      </Paper>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, width: '100%' }}>
      {configToEdit.filters.map((item, key) => (
        <PathItem key={key} index={key} item={item} />
      ))}

      <Button
        variant='outlined'
        startIcon={<AddIcon />}
        onClick={addNewPath}
        size='small'
        sx={{
          textTransform: 'none',
          fontWeight: 500,
          borderStyle: 'dashed',
          mt: 0.5,
          '&:hover': { borderStyle: 'dashed' }
        }}
      >
        Aggiungi Percorso
      </Button>
    </Box>
  )
}

export default PathsSection
